import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentPage = () => {
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [returnPalId, setReturnPalId] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoError, setPromoError] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const plans = {
    'lets-be-pals': { name: "Let's Be Pals", price: 0, monthly: false },
    'your-pal': { name: 'Your Pal', price: 39.99, monthly: true },
    'my-pal': { name: 'My Pal', price: 49.99, monthly: true },
    'best-pals': { name: 'Best Pals', price: 59.99, monthly: true }
  };

  useEffect(() => {
    try {
      // Get booking data from sessionStorage
      const data = sessionStorage.getItem('bookingData');
      if (!data) {

        navigate('/signup');
      } else {
        const parsed = JSON.parse(data);

        setBookingData(parsed);
        // Generate a unique ReturnPal ID for this booking
        const uniqueId = `RP${Date.now().toString().slice(-8)}${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
        setReturnPalId(uniqueId);
      }
    } catch (error) {

      logError('Raw sessionStorage data:', sessionStorage.getItem('bookingData'));
      setError('Failed to load booking data. Please try scheduling your pickup again.');
      // Redirect back to schedule page after 2 seconds
      setTimeout(() => navigate('/schedule-pickup'), 2000);
    }
  }, [navigate]);

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }

    setPromoError('');
    
    // Validate promo code and map to Stripe coupon ID
    const code = promoCode.trim().toUpperCase();
    
    // Map customer-facing codes to Stripe coupon IDs (or to themselves for promotion codes)
    const promoMapping = {
      '50FIRSTPALS': 'first50pals',      // 50% off subscriptions
      'FRIENDSGET5': 'pal4pal',          // 75% off Let's Be Pals
      '15FORPAL': 'pTE2omCR',            // 15% off pickup (one-time)
      'HALFOFFPAL': 'HALFOFFPAL',        // 50% off Let's Be Pals (one-time) — Stripe Promotion Code
      'PAL25': 'Pal25',                  // 25% off first pickup order (first-time customers)
    };
    
    if (promoMapping[code]) {
      setPromoApplied(true);
      setPromoError('');
      setPromoDiscount(0); // Stripe handles discount
      alert(`✅ Promo code "${code}" will be applied at checkout!`);
    } else {
      setPromoError('Invalid promo code. Try: PAL25, HalfOffPal, 50FirstPals, 15ForPal, or FriendsGet5');
      setPromoDiscount(0);
      setPromoApplied(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service and Terms & Conditions before proceeding.');
      return;
    }
    
    setIsProcessing(true);
    setError('');
    
    try {
      const customerData = JSON.parse(sessionStorage.getItem('customerInfo') || '{}');
      const isSubscription = customerData.planType && customerData.planType !== 'lets-be-pals';
      
      let checkoutUrl;
      
      if (isSubscription) {
        // Map customer-facing code to Stripe coupon ID (or itself for Promotion Codes)
        const promoMapping = {
          '50FIRSTPALS': 'first50pals',
          'FRIENDSGET5': 'pal4pal',
          '15FORPAL': 'pTE2omCR',
          'HALFOFFPAL': 'HALFOFFPAL',
          'PAL25': 'Pal25',
        };
        
        const stripeCouponId = promoApplied && promoCode ? promoMapping[promoCode.trim().toUpperCase()] : null;
        
        // Subscription: Pay for first month + add-ons together
        const response = await fetch(`${API_URL}/api/payments/create-subscription-checkout-with-booking`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            plan_type: customerData.planType,
            customer_email: customerData.email,
            customer_name: `${customerData.firstName} ${customerData.lastName}`,
            origin_url: window.location.origin,
            booking_data: bookingData,
            addon_cost: bookingData.totalCost || 0,
            promo_code: stripeCouponId
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to create subscription checkout');
        }
        
        const data = await response.json();
        checkoutUrl = data.checkout_url;
      } else {
        // Pay-as-you-go: Regular checkout
        const promoMapping = {
          '50FIRSTPALS': 'first50pals',
          'FRIENDSGET5': 'pal4pal',
          '15FORPAL': 'pTE2omCR',
          'HALFOFFPAL': 'HALFOFFPAL',
          'PAL25': 'Pal25',
        };
        const stripePromo = promoApplied && promoCode ? promoMapping[promoCode.trim().toUpperCase()] : null;

        const response = await fetch(`${API_URL}/api/payments/create-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            num_packages: bookingData.numberOfPackages,
            rush_dropoff: bookingData.rushDropoff || false,
            origin_url: window.location.origin,
            promo_code: stripePromo,
            booking_data: {
              customerEmail: customerData.email || '',
              locationName: bookingData.locationName || '',
              pickupDate: bookingData.pickupDate || '',
              carrier: bookingData.carrier || '',
              pickupWindow: bookingData.pickupWindow || ''
            }
          })
        });
      
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to create checkout session');
        }
        
        const data = await response.json();
        checkoutUrl = data.checkout_url;
      }
      
      // Redirect to Stripe Checkout
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
      
    } catch (error) {

      setError(error.message || 'Failed to process payment. Please try again.');
      setIsProcessing(false);
    }
  };

  if (!bookingData) {
    return <div>Loading...</div>;
  }

  const customerData = JSON.parse(sessionStorage.getItem('customerInfo') || '{}');

  const planType = customerData.planType || 'lets-be-pals';
  const subscriptionFee = plans[planType]?.price || 0;
  const addonsCost = bookingData.totalCost || 0;
  const totalBeforePromo = subscriptionFee + addonsCost;
  const totalCost = totalBeforePromo - promoDiscount;
  const breakdown = bookingData.pricingBreakdown || {};

  return (
    <div className="signup-page">
      <nav className="nav-header">
        <div className="nav-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_returnpal-pickup/artifacts/x8ux114u_Screenshot%202026-04-27%20165324.png" 
            alt="ReturnPal" 
            className="logo-image" style={{ height: "50px", width: "auto" }}
          />
        </div>
      </nav>

      <div className="payment-container">
        <div className="payment-layout">
          {/* ReturnPal ID Banner — only shown when at least one package is QR */}
          {(() => {
            const packages = bookingData.packages || [];
            const hasQrPackage = packages.some((p) => (p.package_type || 'label') === 'qr');
            const allLabels = packages.length > 0 && packages.every((p) => (p.package_type || 'label') === 'label');

            if (hasQrPackage) {
              return (
                <div style={{
                  gridColumn: '1 / -1',
                  backgroundColor: '#10b981',
                  padding: '16px 24px',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  border: '3px solid #059669',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    color: 'white',
                    flexWrap: 'wrap',
                  }}>
                    <img
                      src="https://customer-assets.emergentagent.com/job_returnpal-pickup/artifacts/x8ux114u_Screenshot%202026-04-27%20165324.png"
                      alt="ReturnPal"
                      style={{ height: '52px', width: 'auto', flexShrink: 0 }}
                      data-testid="returnpal-id-mascot"
                    />
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>Your ReturnPal ID (for QR-code packages)</div>
                      <div style={{ fontSize: '1.8rem', fontWeight: 'bold', letterSpacing: '2px' }}>
                        {returnPalId}
                      </div>
                    </div>
                  </div>
                  <p style={{
                    margin: '8px 0 0',
                    fontSize: '0.85rem',
                    textAlign: 'center',
                    color: 'rgba(255, 255, 255, 0.9)',
                  }}>
                    Write this ID on each <strong>QR-code</strong> package after payment
                  </p>
                </div>
              );
            }

            if (allLabels) {
              return (
                <div style={{
                  gridColumn: '1 / -1',
                  backgroundColor: '#fef3c7',
                  padding: '16px 24px',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  border: '3px solid #f59e0b',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.18)',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}>
                    <img
                      src="https://customer-assets.emergentagent.com/job_returnpal-pickup/artifacts/x8ux114u_Screenshot%202026-04-27%20165324.png"
                      alt="ReturnPal"
                      style={{ height: '52px', width: 'auto', flexShrink: 0 }}
                      data-testid="label-reminder-mascot"
                    />
                    <div style={{ textAlign: 'left', color: '#78350f' }}>
                      <div style={{ fontSize: '0.95rem', fontWeight: '700', marginBottom: '4px' }}>
                        Reminder for label packages
                      </div>
                      <div style={{ fontSize: '0.95rem', lineHeight: 1.45 }}>
                        Write the <strong>last 6 digits of your tracking number</strong> clearly on the outside of each package with a permanent marker.
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* Order Summary */}
          <div className="order-summary">
            <h2 className="payment-heading">Order Summary</h2>
            
            <div className="summary-section">
              <h3 className="summary-title">Pickup Details</h3>
              <div className="summary-item">
                <span>Location:</span>
                <span className="summary-value">{bookingData.locationName}</span>
              </div>
              <div className="summary-item">
                <span>Date:</span>
                <span className="summary-value">{bookingData.pickupDate}</span>
              </div>
              <div className="summary-item">
                <span>Window:</span>
                <span className="summary-value">{bookingData.pickupWindow}</span>
              </div>
              <div className="summary-item">
                <span>Carrier:</span>
                <span className="summary-value">{bookingData.carrier}</span>
              </div>
            </div>

            <div className="summary-section">
              <h3 className="summary-title">Package Details</h3>
              <div className="summary-item">
                <span>Number of packages:</span>
                <span className="summary-value">{bookingData.numberOfPackages}</span>
              </div>
              <div className="summary-item">
                <span>Labels uploaded:</span>
                <span className="summary-value">{bookingData.labelsUploaded}</span>
              </div>
              {bookingData.packagingCount > 0 && (
                <div className="summary-item">
                  <span>Packaging requested:</span>
                  <span className="summary-value">{bookingData.packagingCount} packages</span>
                </div>
              )}
            </div>

            <div className="summary-section pricing-breakdown">
              <h3 className="summary-title">Pricing</h3>
              
              {/* Subscription Fee */}
              {subscriptionFee > 0 && (
                <div className="summary-item" style={{ backgroundColor: '#f0fdf4', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                  <span style={{ fontWeight: '600', color: '#059669' }}>
                    🎉 {plans[planType]?.name} Subscription
                  </span>
                  <span className="summary-value" style={{ fontWeight: '700', color: '#059669' }}>
                    ${subscriptionFee.toFixed(2)}/month
                  </span>
                </div>
              )}
              
              {/* Base pickup cost for pay-as-you-go */}
              {subscriptionFee === 0 && breakdown.baseTier !== undefined && (
                <div className="summary-item">
                  <span>Pickup ({bookingData.numberOfPackages} {bookingData.numberOfPackages === 1 ? 'package' : 'packages'})</span>
                  <span className="summary-value">${breakdown.baseTier.toFixed(2)}</span>
                </div>
              )}
              
              {/* Add-ons header */}
              {addonsCost > 0 && (
                <div style={{ marginTop: '1rem', marginBottom: '0.5rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.5rem' }}>Add-Ons for This Pickup:</h4>
                </div>
              )}
              
              {/* Additional pickup fee */}
              {breakdown.additionalPickupFee > 0 && (
                <div className="summary-item" style={{ color: '#f59e0b' }}>
                  <span>Additional pickup:</span>
                  <span className="summary-value">${breakdown.additionalPickupFee.toFixed(2)}</span>
                </div>
              )}
              
              {/* Additional package fee */}
              {breakdown.additionalPackageFee > 0 && (
                <div className="summary-item" style={{ color: '#f59e0b' }}>
                  <span>📦 Additional packages:</span>
                  <span className="summary-value">${breakdown.additionalPackageFee.toFixed(2)}</span>
                </div>
              )}
              
              {/* Packaging cost */}
              {breakdown.packagingCost > 0 && (
                <div className="summary-item" style={{ color: '#059669' }}>
                  <span>📦 Packaging materials:</span>
                  <span className="summary-value">${breakdown.packagingCost.toFixed(2)}</span>
                </div>
              )}
              
              {/* Oversized fee */}
              {breakdown.oversizedFee > 0 && (
                <div className="summary-item" style={{ color: '#dc2626' }}>
                  <span>📏 Oversized handling:</span>
                  <span className="summary-value">${breakdown.oversizedFee.toFixed(2)}</span>
                </div>
              )}
              
              {/* Rush fee */}
              {breakdown.rushFee > 0 && (
                <div className="summary-item" style={{ color: '#f59e0b' }}>
                  <span>⚡ Rush Drop-Off:</span>
                  <span className="summary-value">${breakdown.rushFee.toFixed(2)}</span>
                </div>
              )}
              
              {/* Promo Code Section */}
              {subscriptionFee > 0 && !promoApplied && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.75rem' }}>Have a Promo Code?</h4>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      placeholder="Enter code (e.g., LAUNCH50)"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        border: '2px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        textTransform: 'uppercase'
                      }}
                    />
                    <Button 
                      type="button"
                      onClick={applyPromoCode}
                      style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                  {promoError && (
                    <p style={{ fontSize: '0.875rem', color: '#dc2626', marginTop: '0.5rem' }}>{promoError}</p>
                  )}
                </div>
              )}
              
              {/* Promo Discount Applied */}
              {promoApplied && promoDiscount > 0 && (
                <div className="summary-item" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb', color: '#10b981' }}>
                  <span style={{ fontWeight: '600' }}>🎉 Promo Code "{promoCode}":</span>
                  <span className="summary-value" style={{ fontWeight: '700' }}>-${promoDiscount.toFixed(2)}</span>
                </div>
              )}
              
              <div className="summary-item summary-total" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #059669' }}>
                <strong>Total Payment Today:</strong>
                <strong className="summary-value" style={{ fontSize: '1.5rem', color: '#059669' }}>${totalCost.toFixed(2)}</strong>
              </div>
              
              {subscriptionFee > 0 && (
                <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.75rem', textAlign: 'center' }}>
                  {promoApplied 
                    ? `Promo applied! First month: $${totalCost.toFixed(2)}. After that: $${subscriptionFee.toFixed(2)}/month + add-ons.`
                    : 'Includes first month subscription + this pickup\'s add-ons. Future pickups only charge add-ons.'}
                </p>
              )}
            </div>
          </div>

          {/* Payment Form */}
          <div className="payment-form-section">
            <h2 className="payment-heading">Payment Information</h2>
            <div className="secure-badge">
              <Lock size={16} />
              <span>Secure payment processing with Stripe</span>
            </div>
            
            {/* Subscription billing note */}
            {breakdown.baseTier === 0 && breakdown.subscriptionSavings > 0 && (
              <div style={{
                marginTop: '1rem',
                marginBottom: '1rem',
                padding: '12px',
                backgroundColor: '#fef3c7',
                borderRadius: '6px',
                border: '1px solid #f59e0b',
                fontSize: '0.9rem',
                color: '#92400e'
              }}>
                <strong>📋 Subscription Billing:</strong> Your monthly subscription fee will be charged separately. Today you're only paying for add-on services.
              </div>
            )}

            {error && (
              <div className="error-banner" style={{
                padding: '12px',
                marginBottom: '20px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '8px',
                color: '#c00'
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="signup-form">
              <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                  You will be redirected to Stripe's secure checkout page to complete your payment.
                </p>
              </div>

              {/* Terms Agreement Checkbox */}
              <div style={{ 
                marginBottom: '1.5rem', 
                padding: '1rem', 
                backgroundColor: '#fffbeb', 
                borderRadius: '8px', 
                border: '1px solid #fbbf24' 
              }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', gap: '0.75rem' }}>
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    style={{ 
                      marginTop: '4px', 
                      width: '18px', 
                      height: '18px', 
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                    required
                  />
                  <span style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#374151' }}>
                    I have read and agree to ReturnPal's{' '}
                    <a 
                      href="/terms-of-service" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#10b981', textDecoration: 'underline', fontWeight: '600' }}
                    >
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a 
                      href="/terms-and-conditions" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#10b981', textDecoration: 'underline', fontWeight: '600' }}
                    >
                      Terms & Conditions
                    </a>
                    . I understand that ReturnPal is an independent logistics convenience service, is not affiliated with retailers or carriers, and does not determine return eligibility, return acceptance, or refund decisions.
                  </span>
                </label>
              </div>

              <Button 
                type="submit" 
                className="btn-primary btn-large w-full"
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <CreditCard size={20} className="spinning" />
                    Redirecting to Stripe...
                  </>
                ) : (
                  <>
                    <Lock size={20} />
                    Proceed to Payment ${totalCost.toFixed(2)}
                  </>
                )}
              </Button>

              <p className="payment-note">
                Your payment information is secure and encrypted. By completing this purchase, you agree to our Terms of Service.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;

